import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * The Money section's four ingest and delivery queues.
 *
 *   `mny-events`   ← GET /v1/admin/provider-events
 *   `mny-webhooks` ← GET /v1/admin/webhooks/deliveries
 *   `mny-enrich`   ← GET /v1/admin/enrichment/tasks
 *   `mny-outbox`   ← GET /v1/admin/notifications/outbox
 *
 * # The backend sends its own disclosures, and they are rendered rather than
 * paraphrased
 *
 * Each of these responses carries prose fields — `retry_note`, `depth_note`,
 * `sequence_note`, `redaction_note`, `state_note`, `severity_note`,
 * `history_note`, `control_note` — written by the handler that knows what the
 * schema cannot answer. They are rendered VERBATIM.
 *
 * That is deliberate and it is not laziness. A paraphrase drifts: the note says
 * "there is no retry count and one cannot be added without a migration — and
 * even then not for these rows", and a console that summarised it as "retries
 * unavailable" would have quietly turned a permanent property of the schema
 * into something that reads as a temporary gap. When the backend's answer
 * changes, the console's changes with it and nobody has to notice.
 *
 * # Four screens the design drew with columns the backend cannot fill
 *
 * The prototype's columns were drawn before these endpoints existed, and three
 * of them ask for figures that do not exist:
 *
 *   - `mny-webhooks` draws **Retries**. `webhook_deliveries` has no attempt
 *     column, and a redelivery is a NEW ROW rather than an increment. Rows could
 *     only be grouped by `institution_event_id`, which is NULL on exactly the
 *     rejected deliveries this queue is for. The column renders as not counted,
 *     with the reason, rather than as `0` or `—`.
 *   - `mny-enrich` draws **Credit posted**. Enrichment carries no posting flag,
 *     and inventing one from the task's state would answer a money question from
 *     a notification queue — the thing invariant 10 and the severity note both
 *     forbid. It renders as a pointer to the screen that can answer.
 *   - `mny-enrich` draws `enrichment_state` as a tri-state. The column has
 *     exactly two values; the queue is claim-and-delete, so an in-flight task is
 *     a `pending` row holding a lock a read cannot see. Two chips, not three.
 *
 * Rendering an absent figure as `0` on any of these is the failure the whole
 * module exists to avoid: on a queue screen `0` reads as "healthy", and three of
 * these four screens are read precisely to decide whether something is stuck.
 *
 * # Minor units arrive as STRINGS and are parsed as BigInt
 *
 * `moneyView.amount_minor` is serialised canonically as a string, because a
 * minor-unit figure can exceed what a JavaScript number holds exactly — and a
 * console that lost a digit would lose it silently. `exactMinorBig` is the only
 * parse used here; a `Number()` anywhere in this file is a bug (invariant 3).
 */

const READ_LIMIT = 100

/** Every screen here is read-only, and the backend says so in `control_note`. */
const AUTHORITY = 'operational'

// ---------------------------------------------------------------------------
// Shared shape helpers
// ---------------------------------------------------------------------------

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

/** A whole number, or null. Rejects floats and non-finite values outright. */
function whole(v: unknown): number | null {
  if (typeof v === 'number' && Number.isSafeInteger(v)) return v
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) {
    const n = Number(v.trim())
    return Number.isSafeInteger(n) ? n : null
  }
  return null
}

function rows(payload: Record<string, unknown>, key: string): readonly unknown[] {
  const v = payload[key]
  return Array.isArray(v) ? v : []
}

/**
 * A timestamp as the backend sent it, trimmed to the minute.
 *
 * Never reformatted into a locale. These screens are read side by side with the
 * institution's own console and with log lines, and a timestamp that has been
 * helpfully localised no longer matches either.
 */
function stamp(v: unknown): string {
  const s = text(v)
  if (s === '') return '— not stated'
  return s.length >= 16 ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : s
}

/**
 * An amount cell from a `moneyView`, or a stated absence.
 *
 * ABSENT IS NOT ZERO here and the distinction is the point: `settled` is absent
 * until the institution settles, and a settled column reading `€0.00` on an
 * authorised-but-unsettled event says the money arrived and was nothing.
 */
function amountCell(view: unknown, absent: string): Cell {
  if (!isObject(view)) return absent
  const currency = text(view['currency'])
  if (!isCurrencyCode(currency)) return '— no currency stated'
  const minor = exactMinorBig(view['amount_minor'])
  if (minor === null) return '— not an exact integer'
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return '— beyond the exact range of an amount cell'
  }
  return [Number(minor), currency]
}

/** A yes/no chip where the backend sent a boolean, and a stated gap where it did not. */
function boolChip(v: unknown, yes: string, no: string, yesTone: 'g' | 'a' | 'r' | 'n' = 'g'): ChipCell {
  if (v === true) return [yes, yesTone]
  if (v === false) return [no, yesTone === 'g' ? 'r' : 'n']
  return ['— not stated', 'n']
}

/** The prose the backend sent, as note blocks, in the order it names them. */
function disclosures(payload: Record<string, unknown>, keys: readonly (readonly [string, string])[]): Block[] {
  const out: Block[] = []
  for (const [key, kicker] of keys) {
    const body = text(payload[key])
    if (body === '') continue
    out.push({ t: 'note', tone: 'n', kicker, title: '', body, ref: key })
  }
  return out
}

/**
 * The refusal path, shared by all four.
 *
 * A loader never throws except on abort, and a refusal renders as `unread` with
 * its `decision_ref` intact (law 13) — never as an empty queue. An empty queue
 * on any of these four screens reads as "nothing is stuck", which is the one
 * conclusion a failed read must never produce.
 */
function refusal(error: unknown, source: string, what: string): ScreenData {
  if (error instanceof DOMException && error.name === 'AbortError') throw error
  const api = error instanceof ApiError ? error : null
  const reason = api === null
    ? 'The backend could not be reached.'
    : api.status === 404
      ? 'This endpoint is not available on this deployment.'
      : `The backend answered ${String(api.status)}${api.code === null ? '' : ` (${api.code})`}.`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: `${what} could not be loaded`,
      body: reason,
      ref: api?.decisionRef === null || api?.decisionRef === undefined
        ? ''
        : `decision_ref ${api.decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

function table(title: string, cols: readonly Column[], body: readonly (readonly Cell[])[], footer: string, foot2: string): TableBlock {
  return { t: 'table', title, src: 'ledger', facets: false, cols, rows: body, footer, foot2 }
}

// ---------------------------------------------------------------------------
// mny-events — the provider event feed
// ---------------------------------------------------------------------------

export const PROVIDER_EVENTS_PATH = `/provider-events?limit=${String(READ_LIMIT)}`

const EVENTS_SOURCE = 'postgres · provider_events (GET /v1/admin/provider-events)'

const EVENT_COLS: readonly Column[] = [
  ['event_id', 'm', 'minmax(150px,1.1fr)'],
  ['Observed', 'm', '146px'],
  ['State', 'c', '116px'],
  ['Direction', 'x', '96px'],
  ['Authorised', 'a', '130px'],
  ['Settled', 'a', '130px'],
  ['Counterparty', 'x', 'minmax(160px,1.2fr)'],
  ['Posted', 'c', '116px'],
]

export function composeProviderEvents(payload: unknown, readAt: Date): ScreenData {
  if (!isObject(payload)) {
    return refusal(null, EVENTS_SOURCE, 'The provider event feed')
  }

  const body: Cell[][] = []
  let unreadable = 0
  for (const raw of rows(payload, 'events')) {
    if (!isObject(raw)) { unreadable += 1; continue }

    // The counterparty is the one field an Art. 17 erasure removes, and an
    // erased row renders as REDACTED rather than as absent — "we never had it"
    // and "we destroyed it on request" are different answers to an auditor.
    const counterparty = raw['counterparty_redacted'] === true
      ? 'REDACTED — erased on request'
      : text(raw['counterparty_name']) === ''
        ? '— none carried'
        : text(raw['counterparty_name'])

    const posted = raw['posted'] === true
    const postingID = whole(raw['posting_id'])
    const age = whole(raw['unposted_age_seconds'])

    body.push([
      text(raw['institution_event_id']) === '' ? '— no event id' : text(raw['institution_event_id']),
      stamp(raw['observed_at']),
      [text(raw['state']) === '' ? '— not stated' : text(raw['state']), 'n'] as ChipCell,
      text(raw['direction']) === '' ? '— not stated' : text(raw['direction']),
      amountCell(raw['authorised'], '— none stated'),
      // Absent, never zero. See amountCell.
      amountCell(raw['settled'], '— not settled yet'),
      counterparty,
      posted
        ? ([postingID === null ? 'posted' : `posting ${String(postingID)}`, 'g'] as ChipCell)
        : ([age === null ? 'not posted' : `unposted ${String(Math.floor(age / 3600))}h`, 'a'] as ChipCell),
    ])
  }

  const depth = whole(payload['unposted_depth'])
  const blocks: Block[] = [
    table(
      'Provider event feed',
      EVENT_COLS,
      body,
      `${String(body.length)} event(s) on this page${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable and withheld`}`,
      depth === null
        ? 'The unposted depth was not stated, so it is unknown rather than zero.'
        : `${String(depth)} unposted event(s) across the whole table under these filters — not this page.`,
    ),
    ...disclosures(payload, [
      ['sequence_note', 'Sequence'],
      ['depth_note', 'Depth'],
      ['redaction_note', 'Redaction'],
      ['export_note', 'Export'],
      ['control_note', 'Controls'],
    ]),
  ]

  return {
    blocks,
    provenance: { kind: 'live', source: EVENTS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
  }
}

export const loadProviderEvents: ScreenLoader = async (api) => {
  try {
    return composeProviderEvents(await api.get<unknown>(PROVIDER_EVENTS_PATH), new Date())
  } catch (error) {
    return refusal(error, EVENTS_SOURCE, 'The provider event feed')
  }
}

// ---------------------------------------------------------------------------
// mny-webhooks — deliveries from the institution
// ---------------------------------------------------------------------------

export const WEBHOOK_DELIVERIES_PATH = `/webhooks/deliveries?limit=${String(READ_LIMIT)}`

const WEBHOOKS_SOURCE = 'postgres · webhook_deliveries (GET /v1/admin/webhooks/deliveries)'

const WEBHOOK_COLS: readonly Column[] = [
  ['ID', 'm', '86px'],
  ['Received', 'm', '146px'],
  ['Provider', 'x', '110px'],
  ['Authenticated', 'c', '130px'],
  ['Parsed', 'c', '110px'],
  ['Retries', 'n', '150px'],
  ['Event', 'm', 'minmax(150px,1.1fr)'],
  ['Payload', 'c', '132px'],
]

export function composeWebhookDeliveries(payload: unknown, readAt: Date): ScreenData {
  if (!isObject(payload)) {
    return refusal(null, WEBHOOKS_SOURCE, 'Webhook deliveries')
  }

  const body: Cell[][] = []
  let unreadable = 0
  for (const raw of rows(payload, 'deliveries')) {
    if (!isObject(raw)) { unreadable += 1; continue }

    const eventID = text(raw['institution_event_id'])
    const erased = raw['body_erased'] === true
    const sealed = raw['body_sealed'] === true

    body.push([
      String(whole(raw['id']) ?? '—'),
      stamp(raw['received_at']),
      text(raw['provider']) === '' ? '— not stated' : text(raw['provider']),
      // Two booleans, two remedies. Unauthenticated is a security matter;
      // authenticated-but-unparsed is an integration one.
      boolChip(raw['authenticated'], 'authenticated', 'UNAUTHENTICATED'),
      boolChip(raw['verified'], 'parsed', 'not parsed'),
      // The design drew a retry count. There is none and there cannot be one
      // for these rows — see the module header and `retry_note`.
      '— not counted',
      eventID === ''
        ? '— none learned'
        : eventID,
      erased
        ? (['erased for ever', 'r'] as ChipCell)
        : sealed
          ? (['sealed · separate grant', 'a'] as ChipCell)
          : (['none stored', 'n'] as ChipCell),
    ])
  }

  const d = isObject(payload['depths']) ? payload['depths'] : {}
  const depth = (k: string) => whole(d[k])
  const stated = (k: string, label: string) => {
    const n = depth(k)
    return n === null ? `${label} unknown` : `${String(n)} ${label}`
  }

  const blocks: Block[] = [
    table(
      'Webhook deliveries',
      WEBHOOK_COLS,
      body,
      `${String(body.length)} delivery(ies) on this page${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable and withheld`}`,
      `Across the whole table under these filters: ${stated('total', 'total')} · ${stated('unauthenticated', 'unauthenticated')} · ${stated('unverified', 'authenticated but unparsed')} · ${stated('without_institution_event', 'with no event id')} · ${stated('body_erased', 'payload erased')}.`,
    ),
    ...disclosures(payload, [
      ['retry_note', 'Why there is no retry count'],
      ['depth_note', 'Depth'],
      ['body_note', 'Payloads'],
      ['control_note', 'Controls'],
    ]),
  ]

  return {
    blocks,
    provenance: { kind: 'live', source: WEBHOOKS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
  }
}

export const loadWebhookDeliveries: ScreenLoader = async (api) => {
  try {
    return composeWebhookDeliveries(await api.get<unknown>(WEBHOOK_DELIVERIES_PATH), new Date())
  } catch (error) {
    return refusal(error, WEBHOOKS_SOURCE, 'Webhook deliveries')
  }
}

// ---------------------------------------------------------------------------
// mny-enrich — the enrichment queue
// ---------------------------------------------------------------------------

export const ENRICHMENT_TASKS_PATH = `/enrichment/tasks?limit=${String(READ_LIMIT)}`

const ENRICH_SOURCE = 'postgres · enrichment_tasks (GET /v1/admin/enrichment/tasks)'

const ENRICH_COLS: readonly Column[] = [
  ['Task', 'm', '86px'],
  ['Provider event', 'm', '120px'],
  ['enrichment_state', 'c', '120px'],
  ['Attempts', 'n', '92px'],
  ['Runs after', 'm', '146px'],
  ['Last failure', 'x', 'minmax(220px,1.6fr)'],
]

export function composeEnrichmentTasks(payload: unknown, readAt: Date): ScreenData {
  if (!isObject(payload)) {
    return refusal(null, ENRICH_SOURCE, 'The enrichment queue')
  }

  const body: Cell[][] = []
  let unreadable = 0
  for (const raw of rows(payload, 'tasks')) {
    if (!isObject(raw)) { unreadable += 1; continue }

    const state = text(raw['state'])
    const attempts = whole(raw['attempts'])
    const err = text(raw['last_error'])

    body.push([
      String(whole(raw['id']) ?? '—'),
      String(whole(raw['provider_event_id']) ?? '—'),
      // Two values only. The designed tri-state cannot be served: the queue is
      // claim-and-delete, so an in-flight task is a `pending` row holding a lock
      // a read cannot distinguish from an unclaimed one.
      state === 'dead'
        ? (['dead', 'r'] as ChipCell)
        : state === 'pending'
          ? (['pending', 'a'] as ChipCell)
          : ([state === '' ? '— not stated' : state, 'n'] as ChipCell),
      attempts === null ? '— not stated' : String(attempts),
      stamp(raw['run_after']),
      // Overwritten on every failure, so this is the LATEST error and not the
      // first — `history_note` says so and is rendered below.
      err === '' ? '— none recorded' : err,
    ])
  }

  const depths = isObject(payload['depths']) ? payload['depths'] : {}
  const depthLine = Object.keys(depths).length === 0
    ? 'No depths were stated, so they are unknown rather than zero.'
    : Object.entries(depths)
      .map(([k, v]) => `${String(whole(v) ?? '?')} ${k}`)
      .join(' · ')

  const blocks: Block[] = [
    // The severity note leads, because this screen's most likely misreading is
    // that a growing depth means missing money. It does not, and the note says
    // where the money question is actually answered.
    ...disclosures(payload, [['severity_note', 'What a depth here does and does not mean']]),
    table(
      'Enrichment queue',
      ENRICH_COLS,
      body,
      `${String(body.length)} task(s) on this page${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable and withheld`}`,
      `Across the whole table: ${depthLine}. Whether the credit behind a task was posted is not answerable from this queue — that is the provider event feed.`,
    ),
    ...disclosures(payload, [
      ['state_note', 'Why there are two states and not three'],
      ['enqueue_note', 'Enqueue'],
      ['history_note', 'Why there is no attempt history'],
      ['control_note', 'Controls'],
    ]),
  ]

  return {
    blocks,
    provenance: { kind: 'live', source: ENRICH_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
  }
}

export const loadEnrichmentTasks: ScreenLoader = async (api) => {
  try {
    return composeEnrichmentTasks(await api.get<unknown>(ENRICHMENT_TASKS_PATH), new Date())
  } catch (error) {
    return refusal(error, ENRICH_SOURCE, 'The enrichment queue')
  }
}

// ---------------------------------------------------------------------------
// mny-outbox — the notification outbox
// ---------------------------------------------------------------------------

export const NOTIFICATION_OUTBOX_PATH = `/notifications/outbox?limit=${String(READ_LIMIT)}`

const OUTBOX_SOURCE = 'postgres · notifications (GET /v1/admin/notifications/outbox)'

const OUTBOX_COLS: readonly Column[] = [
  ['ID', 'm', '86px'],
  ['Customer', 'm', '100px'],
  ['Kind', 'x', 'minmax(170px,1.2fr)'],
  ['State', 'c', '116px'],
  ['Attempts', 'n', '90px'],
  ['Amount', 'a', '128px'],
  ['Last failure', 'x', 'minmax(200px,1.4fr)'],
]

export function composeNotificationOutbox(payload: unknown, readAt: Date): ScreenData {
  if (!isObject(payload)) {
    return refusal(null, OUTBOX_SOURCE, 'The notification outbox')
  }

  const body: Cell[][] = []
  let unreadable = 0
  for (const raw of rows(payload, 'rows')) {
    if (!isObject(raw)) { unreadable += 1; continue }

    const state = text(raw['state'])
    const dead = text(raw['dead_reason'])
    const err = text(raw['last_error'])

    body.push([
      String(whole(raw['id']) ?? '—'),
      String(whole(raw['customer_id']) ?? '—'),
      text(raw['kind']) === '' ? '— not stated' : text(raw['kind']),
      state === 'dead'
        ? (['dead', 'r'] as ChipCell)
        : state === 'sent'
          ? (['sent', 'g'] as ChipCell)
          : ([state === '' ? '— not stated' : state, 'a'] as ChipCell),
      String(whole(raw['attempts']) ?? '—'),
      // A notification may carry the amount it announced. Absent is ordinary —
      // most kinds carry none — so it states that rather than showing zero.
      amountCell(raw['amount'], '— none announced'),
      // The enum is what a retry decision reads; the error is what a person
      // reads. Both are shown, dead reason first, because a dead row's enum is
      // the actionable half.
      dead !== '' ? dead : err === '' ? '— none recorded' : err,
    ])
  }

  /*
   * `by_state` carries EVERY state including the ones at zero, and that is
   * load-bearing: a state omitted at zero renders as an absent series, which
   * reads as "the queue is not running" rather than "nothing is in that state".
   * So every row is rendered, zeros included.
   *
   * `oldest_created_at` is ABSENT on an empty state rather than zero — on an
   * empty queue there is no oldest row, and a zero timestamp renders as the
   * year 1. Stated as such rather than dashed away, because "nothing waiting"
   * and "we did not measure" are different answers about a queue.
   */
  const stateRows: Cell[][] = []
  for (const raw of rows(payload, 'by_state')) {
    if (!isObject(raw)) continue
    const count = whole(raw['count'])
    const oldest = text(raw['oldest_created_at'])
    stateRows.push([
      text(raw['state']) === '' ? '— not stated' : text(raw['state']),
      count === null ? '— not stated' : String(count),
      count === 0 ? 'nothing waiting' : oldest === '' ? '— not stated' : stamp(oldest),
      text(raw['next_run_at']) === '' ? (count === 0 ? 'nothing due' : '— not stated') : stamp(raw['next_run_at']),
    ])
  }

  const blocks: Block[] = [
    table(
      'By state',
      [['State', 'x', 'minmax(140px,1fr)'], ['Count', 'n', '100px'], ['Oldest waiting', 'm', '160px'], ['Next run', 'm', '160px']],
      stateRows,
      `${String(stateRows.length)} state(s), including any at zero`,
      'Every state is listed even when empty. A state omitted at zero would read as "the queue is not running" rather than "nothing is in it".',
    ),
    table(
      'Notification outbox',
      OUTBOX_COLS,
      body,
      `${String(body.length)} notification(s) on this page${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable and withheld`}`,
      'A dead notification means a customer was not told something. It never means the money did not move — the ledger commits before this queue runs (invariant 10).',
    ),
    ...disclosures(payload, [
      ['retry_history_note', 'Why there is no attempt history'],
      ['delivery_note', 'Delivery'],
      ['control_note', 'Controls'],
    ]),
  ]

  return {
    blocks,
    provenance: { kind: 'live', source: OUTBOX_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) },
  }
}

export const loadNotificationOutbox: ScreenLoader = async (api) => {
  try {
    return composeNotificationOutbox(await api.get<unknown>(NOTIFICATION_OUTBOX_PATH), new Date())
  } catch (error) {
    return refusal(error, OUTBOX_SOURCE, 'The notification outbox')
  }
}
